import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LayersIcon from '@mui/icons-material/Layers';
import { RoomService } from '../../api/services/roomService';
import FloorFormModal from '../../components/FloorFormModal';
import AddIcon from '@mui/icons-material/Add';

export default function FloorSetup() {
    const [floors, setFloors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchFloors = async () => {
        setLoading(true);
        try {
            const response = await RoomService.getFloors();
            setFloors(response.data || response);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch floors:', err);
            setError(err.message || 'Failed to load floors. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFloors();
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Property Floors Setup</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined"
                        startIcon={<RefreshIcon />} 
                        onClick={fetchFloors} 
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => setModalOpen(true)}
                    >
                        Add Floor
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <FloorFormModal 
                open={modalOpen} 
                onClose={() => setModalOpen(false)} 
                onRefresh={fetchFloors} 
            />

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }} color="text.secondary">Loading floors...</Typography>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Floor Label</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {floors.length > 0 ? (
                                floors.map((floor) => (
                                    <TableRow key={floor.id} hover>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LayersIcon fontSize="small" color="primary" />
                                                {floor.label || `Floor ${floor.number}`}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={floor.status || 'Active'} 
                                                size="small" 
                                                color={floor.status === 'Active' ? 'success' : 'default'} 
                                                variant="outlined" 
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No floors configured for this property.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
